/* eslint-disable @typescript-eslint/no-explicit-any */
import dynamic from 'next/dynamic';
import Header from '@/components/Internals/Header';
import WrapperContent from '@/components/Layout/WrapperContent';
import { AdContainer } from '@/components/Layout/ItemListLastNews/styles';
import AdsGeneratorMemo from '@/components/Ads/Google/AdsTypeGeneratorsMemo';
import { getCurrentHub, captureMessage } from '@sentry/nextjs';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import useBreakpoint from '@/hooks/useBreakpoint';
import { GridInternal } from './styles';
import MainContent from '../MainContent';
import { SponseredCategoryHeader } from '@/components/Layout/SponsoredCategoryHeader';
import { getSponsoredCategoryOrTypeWithStringToInternal, isSponsoredOrEditorialToInternal } from '@/lib/sponsors';

// Shape mínimo de `newsData.capa` consumido por este componente. Tipado
// localmente porque `newsData` em si continua `any` (débito pré-existente —
// re-tipar exige sincronizar 7 callers diferentes), mas `capa` é o ponto
// crítico desta entrega e merece um contrato explícito.
type CapaData = {
  url?: string;
  alt?: string;
  creditos?: string;
  blurhash?: string;
  srcset?: { [key: number]: string };
  dimensao?: { largura: number; altura: number };
};

interface NewItemProps {
  newsPreview?: boolean;
  newsData: any;
  isCurrentPage?: boolean;
  type?: string;
}

// Fallback degradante de `alt` com normalização de whitespace:
//   1. `alt` curado do Directus
//   2. `creditos` (matéria legada do monolito)
//   3. `titulo` da matéria (última linha de defesa — capa é informativa)
// `.trim()` evita que strings só com espaços (comum em payload de CMS) sejam
// truthy e bloqueiem o fallback semântico para o nível seguinte.
const normalizeAccessibleAlt = (...candidates: Array<string | undefined | null>): string =>
  candidates.map(v => (typeof v === 'string' ? v.trim() : '')).find(Boolean) || '';

export default function NewsItem({ newsData, newsPreview = false, isCurrentPage = false, type = '' }: NewItemProps) {
  const [isTabletOrMobile] = useBreakpoint('tablet');
  const currentSpan = getCurrentHub().getScope()?.getSpan();
  const newsItemSpan = currentSpan && currentSpan.startChild({ op: 'newsItem' });
  const SideContent = dynamic(() => import('@/components/Internals/SideContent'));
  const router = useRouter();
  const { capa, conteudo, podcast, url } = newsData as {
    capa?: CapaData | null;
    conteudo?: any;
    podcast?: any;
    url?: string;
  };
  const hasCoverImage = Boolean(capa?.url);
  const pageUri = url;

  if (!capa) {
    captureMessage('NewsItem renderizado sem capa', {
      level: 'warning',
      tags: {
        url,
        tipo: newsData?.tipo ?? 'unknown',
        tipoPublicacao: newsData?.tipoPublicacao ?? 'unknown',
      },
    });
  }
  const IdsPg = newsData.widget && newsData.widget?.tipo === 'produto' && type === 'analise' ? [newsData.cu, newsData.widget?.dados?.cu] : newsData.cu;

  let categories: string[] = undefined;
  if (newsData?.categorias) categories = newsData?.categorias;
  const mainCategory: string = newsData?.categoriaPrincipal || undefined;

  const publicationType = newsData?.tipoPublicacao;

  // eslint-disable-next-line no-bitwise
  const isAdvertorial = type === 'artigo' && (newsData.flags & 2) !== 0;
  const isOffer = type === 'artigo' && (newsData.flags & 32) !== 0;

  // Categorias patrocinadas (tutoriais/smartphone) mantêm o header de patrocínio
  // legado. A tarja de editoria NÃO passa mais por aqui — é data-driven pela
  // `editoria` (cor + logo do Directus) resolvida no MainContent.
  let isSponsoredOrEditorialCategory = undefined;
  let sponsoredCategory = undefined;

  if (categories && mainCategory) {
    isSponsoredOrEditorialCategory = isSponsoredOrEditorialToInternal(mainCategory, categories, publicationType);

    if ((categories || publicationType) && isSponsoredOrEditorialCategory === 'sponsored') {
      sponsoredCategory = getSponsoredCategoryOrTypeWithStringToInternal(categories, mainCategory, publicationType);
    }
  }

  const shouldRenderAdsGoogle = !isAdvertorial;
  const shouldRenderAdsByType = type !== 'video' && type !== 'podcast' && type !== 'analise' && type !== 'artigo';

  // Artigos (TIPO_ARTIGOS = 8) passam a exibir a newsletter: o tipo "Artigo" do
  // Directus assume o fluxo que antes era de Matérias, e Matéria sempre teve o
  // widget. Mantemos a supressão para Listas, Análises e Tutoriais.
  const enableNewsLetter =
    newsData?.tipoPublicacao !== '11' && // TIPO_LISTAS
    newsData?.tipoPublicacao !== '2' && // TIPO_ANALISES
    newsData?.tipoPublicacao !== '4' && // TIPO_TUTORIAIS
    !isAdvertorial && // Publieditorial é uma flag na matéria.
    !isOffer; // Oferta é uma flag na matéria.

  useEffect(() => {
    const navigate = () => {
      router.push(window.location.href);
    };

    window.onpopstate = navigate;
  });

  return (
    <div className="route-uri" data-uri-page={pageUri}>
      <WrapperContent isOnHeader hasBreadcrumb>
        {isSponsoredOrEditorialCategory === 'sponsored' && <SponseredCategoryHeader sponsoredCategory={sponsoredCategory} />}
        <Header
          breadcrumb={newsData.breadcrumb}
          titulo={newsData.titulo}
          publicacao={newsData.publicacao}
          isAdvertorial={isAdvertorial}
          isSubDomain={Boolean(newsData.dominio)}
          type={type}
        />

        <GridInternal>
          <MainContent
            // `alt` com fallback degradante em três níveis: `capa.alt` →
            // `capa.creditos` → `newsData.titulo`. Capa de notícia é imagem
            // informativa (WCAG 2.1 §1.1.1), nunca decorativa — evita
            // `alt=""` que silenciaria a imagem para leitores de tela.
            // Guard `hasCoverImage` protege contra matérias sem `imagem_capa`
            // (ex.: tipo vídeo + payload legado sem thumbnail) — não monta
            // `headerImgData` quebrado que faria preload de URL vazia.
            headerImgData={
              hasCoverImage && capa
                ? {
                    url: capa.url as string,
                    alt: normalizeAccessibleAlt(capa.alt, capa.creditos, newsData?.titulo),
                    caption: capa.creditos || '',
                    blurhash: capa.blurhash,
                    srcset: capa.srcset,
                  }
                : undefined
            }
            article={conteudo}
            fonte={newsData.fonte}
            mainCategory={mainCategory}
            widget={newsData?.widget || conteudo.widget}
            video={newsData?.video}
            citados={newsData?.citados}
            capa={newsData.capa}
            podcast={podcast}
            titulo={newsData.titulo}
            description={newsData.tituloAlt}
            url={newsData.encurtador}
            type={type}
            placeholderAds={newsPreview}
            shouldRenderAdsGoogle={shouldRenderAdsGoogle}
            widgetBf={newsData?.conteudo?.blocoBlackFriday}
            isSubDomain={Boolean(newsData.dominio)}
            flags={newsData.flags}
            precos={newsData?.precos}
            editoria={newsData?.editoria}
            parceiros={newsData?.parceiros}
            isHeader="internal"
            isNewsOpen={isAdvertorial}
            enableNewsLetter={enableNewsLetter && isTabletOrMobile}
          />
          {!isTabletOrMobile && <SideContent placeholderAds={newsPreview} shouldRenderAds={shouldRenderAdsGoogle} enableNewsLetter={enableNewsLetter} />}
        </GridInternal>
      </WrapperContent>
      {!newsPreview && shouldRenderAdsByType && (
        <AdContainer>
          {shouldRenderAdsByType && (
            <>
              <AdsGeneratorMemo type="billboard/footer" />
              <AdsGeneratorMemo type="rectangle/footer" />
            </>
          )}
        </AdContainer>
      )}
      {newsItemSpan && newsItemSpan.finish()}
    </div>
  );
}
